<template>
  <div
    class="fixed left-0 right-0 z-10 py-2 flex flex-col px-2 border bg-base-100"
    :style="{ bottom: `${keyboardHeight}px` }"
  >
    <template v-if="needShowAiWrite">
      <div
        class="flex items-center border-b pb-2 mb-2 w-full justify-between"
        v-if="needShowMailAndJournalSelect"
      >
        <div class="font-semibold">
          {{
            totalCount
              ? $t(`mission.carryItems.alreadySelect`, { count: totalCount })
              : $t(`mission.carryItems.select`)
          }}
        </div>
        <SelectBackground
          title="选择前情"
          btn-class="btn-xs w-full  text-right"
          :mate-id="
          modalStore.richTextOption?.senderId ??
          modalStore.richTextOption?.mateId!
        "
          :recipient-id="modalStore.richTextOption?.recipientId"
          v-model:journalzz="source.journalzz"
          v-model:mailzz="source.mailzz"
          v-model:dispatchzz="source.dispatchzz"
          v-model:memoryzz="source.memoryzz"
          :type-list="['journal', 'mail', 'dispatch', 'memory']"
          is-vertical
        />
      </div>
      <div class="flex items-center border-b pb-2 mb-2 justify-end">
        <GenerateAITextBtnGroup :editor="editor" :source="source" />
      </div>
    </template>
    <div class="flex items-center justify-between">
      <div>
        <button
          v-if="modalStore.richTextOption?.entityType"
          class="btn btn-xs rounded"
          @click="showMediaSelectPopup = true"
        >
          <i class="iconify mingcute--pic-2-line" />
        </button>
        <button class="btn btn-xs rounded" @click="editor?.commands.undo()">
          <i class="iconify mingcute--back-line" />
        </button>
        <button class="btn btn-xs rounded" @click="editor?.commands.redo()">
          <i class="iconify mingcute--back-line scale-x-[-1]" />
        </button>

        <template v-if="modalStore.richTextType === 'richText'">
          <button
            v-if="editor"
            @click="
              editor.commands.toggleBulletList(), editor?.commands.focus()
            "
            :class="[
              'btn btn-xs  rounded',
              editor.isActive('bulletList') ? 'btn-primary' : 'btn-ghost',
            ]"
          >
            <i class="iconify mingcute--indent-increase-line" />
          </button>

          <button
            v-if="editor"
            @click="editor.commands.toggleBold(), editor?.commands.focus()"
            :class="[
              'btn btn-xs rounded',
              editor.isActive('bold') ? 'btn-primary' : 'btn-ghost',
            ]"
          >
            <i class="iconify mingcute--bold-line" />
          </button>

          <button
            v-if="editor"
            @click="editor.commands.toggleItalic(), editor?.commands.focus()"
            :class="[
              'btn btn-xs  rounded',
              editor.isActive('italic') ? 'btn-primary' : 'btn-ghost',
            ]"
          >
            <i class="iconify mingcute--italic-line" />
          </button>

          <button
            v-if="editor"
            @click="editor.commands.toggleHighlight(), editor?.commands.focus()"
            :class="[
              'btn btn-xs  rounded',
              editor.isActive('highlight') ? 'btn-primary' : 'btn-ghost',
            ]"
          >
            <i class="iconify mingcute--paint-brush-fill" />
          </button>
        </template>
      </div>

      <div class="flex items-center gap-2">
        <p
          v-if="modalStore.richTextType === 'longText'"
          :class="[
            'text-xs text-base-content/80',
            editor?.storage.characterCount.characters() >=
            modalStore.richTextMaxLength!
              ? 'text-error'
              : '',
          ]"
        >
          {{ editor?.storage.characterCount.characters() }}/{{
            modalStore.richTextMaxLength
          }}
        </p>
        <button class="btn btn-xs rounded" @click="clickSave">
          <i class="iconify mingcute--align-arrow-down-line"></i>
          {{ $t("Buttons.close") }}
        </button>
      </div>
    </div>

    <media-select-popup
      v-model:show="showMediaSelectPopup"
      :max-count="1"
      :multiple="false"
      :enable-mate-gallery="modalStore.richTextOption?.entityType === 'mate'"
      :enable-module-gallery="modalStore.richTextOption?.entityType !== 'mate'"
      :upload-type="0"
      media-type="img"
      replace
      :entity-id="modalStore.richTextOption?.entityId"
      :entity-type="modalStore.richTextOption?.entityType"
      @on-media-selected="onMediaSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { AIWriteType } from "@/enums/mateEnums";
import { useGlobalModal } from "@/stores/globalModal";
import { useMate } from "@/stores/mate";
import { MediaUploadFileItem } from "@/types/component";
import { Editor } from "@tiptap/vue-3";
import {
  onMounted,
  ref,
  onBeforeUnmount,
  nextTick,
  computed,
  watch,
} from "vue";
import {
  MateDispatch,
  MateJournal,
  MateMail,
  MateMemory,
} from "@/types/entity";

const props = defineProps<{
  editor: Editor;
}>();

const emit = defineEmits<{
  (e: "save"): void;
}>();

const keyboardHeight = ref(0);
const modalStore = useGlobalModal();
const showMediaSelectPopup = ref(false);

const mateStore = useMate();

const source = ref({
  journalzz: [] as MateJournal[],
  mailzz: [] as MateMail[],
  dispatchzz: [] as MateDispatch[],
  memoryzz: [] as MateMemory[],
});

watch(
  () => modalStore.showRichTextEditor,
  (val) => {
    if (val) {
      source.value = {
        journalzz: [],
        mailzz: [],
        dispatchzz: [],
        memoryzz: [],
      };
    }
  }
);

const totalCount = computed(() => {
  return (
    (source.value.journalzz?.length || 0) +
    (source.value.mailzz?.length || 0) +
    (source.value.dispatchzz?.length || 0) +
    (source.value.memoryzz?.length || 0)
  );
});
const needShowAiWrite = computed(() => {
  return (
    [
      AIWriteType.MateMail,
      AIWriteType.Roleplay,
      AIWriteType.Module,
      AIWriteType.Moment,
    ].includes(modalStore.richTextAiType!) ||
    (modalStore.richTextAiType === AIWriteType.Reply &&
      !mateStore.currentMate?.isUser)
  );
});

const needShowMailAndJournalSelect = computed(() => {
  return [AIWriteType.MateMail, AIWriteType.Reply, AIWriteType.Moment].includes(
    modalStore.richTextAiType!
  );
});

const onMediaSelected = (mediaList: MediaUploadFileItem[]) => {
  if (mediaList.length > 0) {
    props.editor.commands.setImage({
      // @ts-ignore 兼容数据处理
      src: mediaList[0].url || mediaList[0].media.url,
    });
    props.editor.commands.enter();
  }
};

const clickSave = () => {
  emit("save");
  nextTick(() => {
    modalStore.showRichTextEditor = false;
  });
};

const handleResize = () => {
  if (window.visualViewport) {
    // 计算键盘高度
    const currentHeight = window.visualViewport.height;
    const windowHeight = window.innerHeight;

    // 如果视口高度小于窗口高度，说明键盘弹出
    if (currentHeight < windowHeight) {
      keyboardHeight.value = windowHeight - currentHeight;
    } else {
      keyboardHeight.value = 0;
    }
  }
};

onMounted(() => {
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", handleResize);
  } else {
    window.addEventListener("resize", handleResize);
  }
});

onBeforeUnmount(() => {
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", handleResize);
  } else {
    window.removeEventListener("resize", handleResize);
  }
});
</script>
